// src/pages/Login.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import config from '../twh/config';



import logo from '../assets/icons/tpb-logo-black.svg'


export default function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const token = searchParams.get('token');

  


  const [formType, setFormType] = useState(token ? 'reset' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' }); // success | error

  // Login form fields
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    remember: false,
  });

  // Reset password form fields
  const [resetData, setResetData] = useState({
    password: '',
    password2: '',
  });

  // Forgot password email
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (token) {
      setFormType('reset');
    } else {
      setFormType('login');
    }
  }, [token]);

  const handleLoginChange = (e) => {
    const { name, value, checked, type } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleResetChange = (e) => {
    const { name, value } = e.target;
    setResetData((prev) => ({ ...prev, [name]: value }));
  };

  const handleForgotChange = (e) => {
    setForgotEmail(e.target.value);
  };

  const showAlert = (text, type = 'error') => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 6000);
  };

  // ────────────────────────────────────────────────
  //                  LOGIN SUBMIT
  // ────────────────────────────────────────────────
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('issetLogin', '1');
    formData.append('email', loginData.email.trim());
    formData.append('password', loginData.password);
    formData.append('remember', loginData.remember ? 'remember' : '');

    try {
      const res = await fetch(
        config.reactApi_baseUrl + '/react-api/login/user-auth.php',
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      const data = await res.json();

      if (data.status === true) {
        // Redirect to dashboard / main page
        window.location.href = '/website-data';
        // or use: navigate('/website-data', { replace: true });
      } else {
        showAlert(data.errorMsg?.[0] || 'Invalid credentials. Please try again.', 'error');
      }
    } catch (err) {
      showAlert('Network error. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //             FORGOT PASSWORD SUBMIT
  // ────────────────────────────────────────────────
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return showAlert('Please enter your email', 'error');

    setForgotLoading(true);

    const formData = new FormData();
    formData.append('issetForgetPassword', '1');
    formData.append('email', forgotEmail.trim());

    try {
      const res = await fetch(
        config.reactApi_baseUrl + '/react-api/login/forget-password.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await res.json();

      if (data.status === true) {
        showAlert('Check your inbox for further instructions', 'success');
        setForgotEmail('');
      } else {
        showAlert(data.errorMsg?.[0] || 'Something went wrong.', 'error');
      }
    } catch (err) {
      showAlert('Failed to send reset link. Try again later.', 'error');
    } finally {
      setForgotLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //             CHANGE PASSWORD SUBMIT
  // ──────────────────────────────────────────────── 
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (resetData.password !== resetData.password2) {
      showAlert('Passwords do not match', 'error');
      setLoading(false);
      return;
    }

    if (resetData.password.length < 6) {
      showAlert('Password must be at least 6 characters', 'error');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('issetChangePassword', '1');
    formData.append('token', token);
    formData.append('password', resetData.password);
    formData.append('password2', resetData.password2);

    try {
      const res = await fetch(
        config.reactApi_baseUrl + '/react-api/login/change-password.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await res.json();

      if (data.status === true) {
        showAlert('Password changed successfully! Redirecting...', 'success');

        // Clear token from URL and redirect to login
        setTimeout(() => {
          setSearchParams({});
          setFormType('login');
          setResetData({ password: '', password2: '' });
        }, 2200);
      } else {
        showAlert(data.errorMsg?.[0] || 'Failed to change password', 'error');
      }
    } catch (err) {
      showAlert('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: 'flex',
          width: { xs: '100%', md: '900px' },
          maxWidth: '100%',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Left - Form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
            {formType === 'reset' ? 'Reset Your Password' : 'User Panel'}
          </Typography>

          {message.text && (
            <Alert
              severity={message.type}
              sx={{ mb: 3 }}
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          )}

          {formType === 'login' && (
            <Box component="form" onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange}
                margin="normal"
                required
                autoFocus
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={handleLoginChange}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="remember"
                      checked={loginData.remember}
                      onChange={handleLoginChange}
                      size="small"
                    />
                  }
                  label="Remember me"
                />

                <Link
                  href="#"
                  underline="hover"
                  onClick={() => setFormType('forgot')}
                  sx={{ fontSize: '0.9rem' }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ py: 1.5, textTransform: 'none', fontWeight: 600 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login to continue'}
              </Button>
            </Box>
          )}

          {formType === 'forgot' && (
            <Box component="form" onSubmit={handleForgotSubmit}>
              <TextField
                fullWidth
                label="Enter your email"
                type="email"
                value={forgotEmail}
                onChange={handleForgotChange}
                margin="normal"
                required
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={forgotLoading}
                sx={{ mt: 3, py: 1.5 }}
              >
                {forgotLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box textAlign="center" mt={2}>
                <Link
                  href="#"
                  underline="hover"
                  onClick={() => setFormType('login')}
                >
                  Back to login
                </Link>
              </Box>
            </Box>
          )}

          {formType === 'reset' && (
            <Box component="form" onSubmit={handleResetSubmit}>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={resetData.password}
                onChange={handleResetChange}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="password2"
                type={showPassword ? 'text' : 'password'}
                value={resetData.password2}
                onChange={handleResetChange}
                margin="normal"
                required
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Change Password'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Right - Illustration / Branding */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'grey.200',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
          }}
        >
          <Box
            component="img"
            src={logo} // ← adjust path if needed
            alt="Login illustration"
            sx={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain' }}
          />
        </Box>
      </Paper>
    </Box>
  );
}